/* Detects a real Supabase OAuth result in the current URL.
 *
 * The Orion admin login is the only Supabase OAuth flow in this app, and it runs
 * implicit flow (supabase-js default, no `flowType` override) — so a successful
 * callback returns tokens in the URL *hash* (`#access_token=...`), a PKCE callback
 * (if ever enabled) would put `?code=...` in the query, and a provider failure
 * returns `?error=...&error_description=...`.
 *
 * We intentionally require a real OAuth descriptor: a bare `?error=` (which any
 * unrelated page might carry) must NOT be treated as an auth callback, and the
 * bare pathname is never a signal. This keeps normal page loads untouched.
 */
export function isSupabaseAuthCallback(search: string, hash: string): boolean {
  const query = new URLSearchParams(search.replace(/^\?/, ''));
  const fragment = new URLSearchParams(hash.replace(/^#/, ''));
  const has = (key: string) => query.has(key) || fragment.has(key);

  // Success: implicit-flow tokens (hash) or a PKCE code (query).
  if (has('access_token') || has('provider_token') || has('code')) return true;

  // Failure: an OAuth error must carry a descriptor, never a bare `?error=`.
  if (has('error') && (has('error_description') || has('error_code'))) return true;

  return false;
}
